import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { printerFeatureKey, PrinterState } from '../../store/printer/printer.reducer';
import { connectPrinterSuccess, connectPrinterFailure, loadPrinterSuccess, loadPrinterFailure } from '../../store/printer/printer.actions';

@Injectable({
  providedIn: 'root',
})
export class PrinterService {
  private device: USBDevice | null = null;
  private endpointNumber: number | null = null;
  private readonly ESC = 0x1b;
  private readonly GS = 0x1d;
  private readonly LF = 0x0a;
  private readonly CUT_PAPER = new Uint8Array([this.GS, 0x56, 0x41, 0x00]);
  private readonly OPEN_DRAWER = new Uint8Array([this.ESC, 0x70, 0x00, 0x19, 0xFA]);
  private readonly CHARACTERS_PER_LINE = 48;
  isConnected$!: Observable<boolean>;

  // Para la detección automática
  private connectedDevices: USBDevice[] = [];
  private detectionInProgress = false;
  private detectionComplete = new Subject<USBDevice | null>();

  // Para guardar el último dispositivo utilizado
  private lastAccessibleDevices: USBDevice[] = [];

  // Variable para controlar si estamos en la pantalla de login
  private isInLoginScreen = false;

  // Variable para controlar si la impresora ha sido ignorada en esta sesión
  private static printerIgnoredInSession = false;

  constructor(private store: Store<{ printer: PrinterState }>) {
    this.isConnected$ = this.store.pipe(select(printerFeatureKey), select('isConnected'));

    // Verificar la ruta actual para determinar si estamos en la pantalla de login
    // Esto es una simplificación - en una implementación real, deberíamos
    // suscribirnos al router para detectar cambios de ruta
    this.checkIfInLoginScreen();

    // Verificar si la impresora ya ha sido ignorada en localStorage
    this.checkIfPrinterIgnored();

    // Solo iniciar la detección de dispositivos si no estamos en la pantalla de login
    // y la impresora no ha sido ignorada en esta sesión
    if (!this.isInLoginScreen && !PrinterService.printerIgnoredInSession) {
      console.log('No estamos en la pantalla de login y la impresora no ha sido ignorada, iniciando detección de dispositivos USB');
      this.initializeUSBDetection();
    } else if (this.isInLoginScreen) {
      console.log('Estamos en la pantalla de login, no se iniciará la detección de dispositivos USB');
    } else if (PrinterService.printerIgnoredInSession) {
      console.log('La impresora ha sido ignorada en esta sesión, no se iniciará la detección de dispositivos USB');
    }

    // Escuchar cambios en la URL para detectar cambios de ruta
    window.addEventListener('popstate', () => {
      this.handleRouteChange();
    });

    // También podemos detectar cambios de ruta mediante clicks en enlaces
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'A' || target.closest('a')) {
        // Esperar a que la navegación se complete
        setTimeout(() => {
          this.handleRouteChange();
        }, 100);
      }
    });
  }

  /**
   * Maneja los cambios de ruta
   */
  private handleRouteChange(): void {
    // Actualizar el estado de la pantalla de login
    const wasInLoginScreen = this.isInLoginScreen;
    this.checkIfInLoginScreen();

    // Si salimos de la pantalla de login y la impresora no ha sido ignorada,
    // iniciar la detección de dispositivos
    if (wasInLoginScreen && !this.isInLoginScreen && !PrinterService.printerIgnoredInSession) {
      console.log('Saliendo de la pantalla de login, iniciando detección de dispositivos USB');
      this.initializeUSBDetection();
    }
  }

  /**
   * Verifica si la impresora ha sido marcada como ignorada en localStorage
   */
  private checkIfPrinterIgnored(): void {
    const printerInfoString = localStorage.getItem('printerDevice');
    if (printerInfoString) {
      try {
        const printerInfo = JSON.parse(printerInfoString);
        if (printerInfo.ignored) {
          console.log('La impresora ha sido marcada como ignorada en localStorage');
          PrinterService.printerIgnoredInSession = true;

        }
      } catch (e) {
        console.error('Error al procesar la información de la impresora guardada:', e);
      }
    }
  }

  /**
   * Verifica si la aplicación está actualmente en la pantalla de login
   * basándose en la URL actual
   */
  private checkIfInLoginScreen(): void {
    // Verificar si la URL actual contiene '/login' o termina en '/auth'
    const currentUrl = window.location.href;
    this.isInLoginScreen = currentUrl.includes('/login') ||
                          currentUrl.endsWith('/auth') ||
                          currentUrl.endsWith('/auth/');

    console.log('Verificación de pantalla de login:', this.isInLoginScreen ? 'Estamos en login' : 'No estamos en login');
  }

  /**
   * Inicializa la detección de dispositivos USB
   */
  private initializeUSBDetection(): void {
    // Inicializar lista de dispositivos disponibles
    this.refreshConnectedDevices();

    // Intentar detectar dispositivos cada 2 segundos (limitado a 5 intentos)
    let attempts = 0;
    const maxAttempts = 5;
    const intervalId = setInterval(() => {
      attempts++;
      if (attempts >= maxAttempts) {
        clearInterval(intervalId);
        console.log('Se alcanzó el máximo número de intentos de detección de dispositivos USB');
        return;
      }

      this.refreshConnectedDevices();

      // Si ya encontramos dispositivos, cancelar el intervalo
      if (this.lastAccessibleDevices.length > 0) {
        clearInterval(intervalId);
        console.log('Detección de dispositivos USB completada con éxito');
      }
    }, 2000);
  }

  /**
   * Actualiza la lista de dispositivos USB conectados
   */
  private refreshConnectedDevices(): void {
    // Verificar primero si la impresora ha sido ignorada en esta sesión
    if (PrinterService.printerIgnoredInSession) {
      console.log('Impresora ignorada en esta sesión. No se intentará la reconexión automática.');
      return;
    }

    (navigator as any).usb.getDevices().then((devices: USBDevice[]) => {
      this.lastAccessibleDevices = devices;
      console.log('Dispositivos USB disponibles actualizados:', devices.length);

      // Si encontramos dispositivos, intentamos reconectar a la impresora guardada
      if (devices.length > 0) {
        const printerInfoString = localStorage.getItem('printerDevice');
        if (printerInfoString) {
          try {
            const printerInfo = JSON.parse(printerInfoString);

            // Verificar si el usuario ha marcado la impresora como "ignorada" en localStorage
            if (printerInfo.ignored) {
              console.log('Impresora marcada como ignorada en localStorage. No se intentará la reconexión automática.');
              PrinterService.printerIgnoredInSession = true; // Actualizar también la variable de sesión
              return;
            }

            // Buscar el dispositivo usando múltiples identificadores
            const storedDevice = devices.find((device: USBDevice) => {
              // Verificar si estamos usando el formato antiguo (solo printerId)
              if (printerInfo.printerId) {
                return device.productName === printerInfo.printerId ||
                       device.serialNumber === printerInfo.printerId ||
                       `vid_${device.vendorId}_pid_${device.productId}` === printerInfo.printerId;
              }

              // Usar el nuevo formato con múltiples identificadores
              return (
                // Coincidencia exacta por vendorId y productId (más confiable)
                (device.vendorId === printerInfo.vendorId && device.productId === printerInfo.productId) ||
                // Coincidencia por serialNumber si está disponible
                (printerInfo.serialNumber && device.serialNumber === printerInfo.serialNumber) ||
                // Coincidencia por productName si está disponible y no es "Dispositivo desconocido"
                (printerInfo.productName &&
                 device.productName &&
                 device.productName !== 'Dispositivo desconocido' &&
                 device.productName === printerInfo.productName) ||
                // Coincidencia por uniqueId como último recurso
                `vid_${device.vendorId}_pid_${device.productId}` === printerInfo.uniqueId
              );
            });

            if (storedDevice) {
              console.log('Encontrada impresora guardada:', storedDevice.productName || `vid_${storedDevice.vendorId}_pid_${storedDevice.productId}`);
              this.connectToDetectedPrinter(storedDevice)
                .then(success => {
                  if (success) {
                    console.log('Reconexión automática exitosa a la impresora guardada');
                  }
                })
                .catch(err => {
                  console.error('Error en reconexión automática:', err);
                });
            } else {
              console.log('No se encontró la impresora guardada entre los dispositivos disponibles');
            }
          } catch (e) {
            console.error('Error al procesar la información de la impresora guardada:', e);
          }
        }
      }
    }).catch((err: any) => {
      console.error('Error al inicializar lista de dispositivos:', err);
    });
  }

  printerIsConnected(): Observable<boolean> {
    return this.isConnected$;
  }

  /**
   * Inicia el proceso de detección automática de la impresora
   * Puede funcionar en modo silencioso (para detección automática) o mostrar diálogo (para selección manual)
   * @param skipDialog Si es true, no mostrará el diálogo de selección (útil para detección automática)
   */
  async startPrinterDetection(skipDialog: boolean = false): Promise<Observable<USBDevice | null>> {
    // Verificar primero si la impresora ha sido ignorada en esta sesión
    if (PrinterService.printerIgnoredInSession) {
      console.log('Impresora ignorada en esta sesión. No se iniciará la detección.');
      this.detectionComplete = new Subject<USBDevice | null>();
      this.detectionComplete.next(null);
      this.detectionComplete.complete();
      return this.detectionComplete.asObservable();
    }

    this.detectionInProgress = true;
    this.detectionComplete = new Subject<USBDevice | null>();

    try {
      // En modo normal (manual), mostrar diálogo de selección
      if (!skipDialog) {
        try {
          console.log('🔍 Mostrando diálogo de selección de dispositivo USB...');
          const device = await (navigator as any).usb.requestDevice({
            filters: [] // Sin filtros para permitir seleccionar cualquier dispositivo
          });

          console.log('✅ Permiso concedido para el dispositivo:', device.productName);

          if (device) {
            const connected = await this.connectToDetectedPrinter(device);
            if (connected) {
              this.detectionComplete.next(device);
              this.detectionComplete.complete();
              return this.detectionComplete.asObservable();
            }
          }
        } catch (error) {
          console.log('❌ Usuario canceló la selección o no hay dispositivos disponibles');
        }
      }
      // En modo automático, usar una estrategia simplificada que resuelva de forma inmediata
      else {
        console.log('⌛ Iniciando detección simplificada para modo automático...');

        try {
          console.log('📣 PASO 1: Solicitando permiso via requestDevice...');

          // IMPORTANTE: La API WebUSB obliga a solicitar permiso con interacción del usuario
          try {
            const device = await (navigator as any).usb.requestDevice({ filters: [] });
            console.log('✅ Permiso concedido, dispositivo seleccionado:', device.productName);

            // Si el usuario seleccionó un dispositivo, intenta conectarse a él directamente
            const connected = await this.connectToDetectedPrinter(device);
            if (connected) {
              console.log('🎯 Conectado exitosamente tras selección manual');
              this.detectionComplete.next(device);
              this.detectionComplete.complete();
              return this.detectionComplete.asObservable();
            }
          } catch (e) {
            console.log('⚠️ Usuario canceló selección, procediendo con reconocimiento del dispositivo recién conectado');
          }

          console.log('📣 PASO 2: Obteniendo dispositivos disponibles...');

          // Obtener dispositivos a los que ya tenemos permiso (después de requestDevice)
          const devices = await (navigator as any).usb.getDevices();
          console.log(`📊 Dispositivos disponibles: ${devices.length}`);

          // Si tenemos dispositivos disponibles, intenta conectarse al más reciente
          if (devices.length > 0) {
            // Usar el último dispositivo detectado
            const device = devices[devices.length - 1];
            console.log('🎯 Intentando conectar al dispositivo disponible:', device.productName);

            const connected = await this.connectToDetectedPrinter(device);
            if (connected) {
              console.log('✅ Conexión exitosa al dispositivo disponible');
              this.detectionComplete.next(device);
              this.detectionComplete.complete();
              return this.detectionComplete.asObservable();
            }
          }

          // Si no hay dispositivos disponibles o falló la conexión, inicia un sondeo breve
          console.log('📣 PASO 3: No hay dispositivos disponibles, esperando con polling rápido...');

          let attempts = 0;
          const maxAttempts = 5;

          const quickPoll = setInterval(async () => {
            attempts++;

            if (!this.detectionInProgress || attempts > maxAttempts) {
              clearInterval(quickPoll);
              if (this.detectionInProgress) {
                console.log('⏱️ Polling finalizado sin resultados');
                this.detectionInProgress = false;
                this.detectionComplete.next(null);
                this.detectionComplete.complete();
              }
              return;
            }

            try {
              const currentDevices = await (navigator as any).usb.getDevices();
              console.log(`🔍 Intento ${attempts}/${maxAttempts}: ${currentDevices.length} dispositivos`);

              if (currentDevices.length > 0) {
                clearInterval(quickPoll);

                // Intentar con el dispositivo más reciente
                const device = currentDevices[currentDevices.length - 1];
                console.log('🎯 Dispositivo encontrado en polling:', device.productName);

                const connected = await this.connectToDetectedPrinter(device);
                this.detectionInProgress = false;

                if (connected) {
                  console.log('✅ Conexión exitosa en polling');
                  this.detectionComplete.next(device);
                } else {
                  console.log('❌ Fallo al conectar en polling');
                  this.detectionComplete.next(null);
                }

                this.detectionComplete.complete();
              }
            } catch (err: any) {
              console.error('❌ Error en polling rápido:', err);
            }
          }, 1000); // Intervalo de 1 segundo

          // Timeout
          setTimeout(() => {
            if (this.detectionInProgress) {
              clearInterval(quickPoll);
              this.detectionInProgress = false;
              console.log('⏱️ Tiempo de detección agotado');
              this.detectionComplete.next(null);
              this.detectionComplete.complete();
            }
          }, 10000); // 10 segundos máximo
        } catch (error) {
          console.error('❌ Error en detección automática:', error);
          this.detectionInProgress = false;
          this.detectionComplete.next(null);
          this.detectionComplete.complete();
        }
      }

      return this.detectionComplete.asObservable();
    } catch (error) {
      console.error('❌ Error general en detección de impresora:', error);
      this.detectionInProgress = false;
      this.detectionComplete.next(null);
      this.detectionComplete.complete();
      return this.detectionComplete.asObservable();
    }
  }

  /**
   * Cancela el proceso de detección automática
   */
  cancelPrinterDetection(): void {
    this.detectionInProgress = false;
  }

  /**
   * Conecta a la impresora detectada automáticamente
   */
  private async connectToDetectedPrinter(device: USBDevice): Promise<boolean> {
    try {
      console.log('PrinterService: connectToDetectedPrinter - Abriendo dispositivo...');
      await device.open();
      console.log('PrinterService: connectToDetectedPrinter - Dispositivo abierto.');

      console.log('PrinterService: connectToDetectedPrinter - Seleccionando configuración e interfaz...');
      await this.selectConfigurationAndInterface(device);
      console.log('PrinterService: connectToDetectedPrinter - Configuración e interfaz seleccionadas.');

      console.log('PrinterService: connectToDetectedPrinter - Reseteando dispositivo...');
      await this.reset(device);
      console.log('PrinterService: connectToDetectedPrinter - Dispositivo reseteado.');

      this.device = device;

      console.log('PrinterService: connectToDetectedPrinter - device.productName:', device.productName);
      console.log('PrinterService: connectToDetectedPrinter - device.serialNumber:', device.serialNumber);
      console.log('PrinterService: connectToDetectedPrinter - device.vendorId:', device.vendorId);
      console.log('PrinterService: connectToDetectedPrinter - device.productId:', device.productId);

      // Guardar la información completa del dispositivo en localStorage
      const printerInfo = {
        // Identificadores principales
        productName: device.productName || 'Dispositivo sin nombre',
        serialNumber: device.serialNumber || '',
        vendorId: device.vendorId,
        productId: device.productId,
        // Identificador único compuesto (para casos donde no hay nombre ni serial)
        uniqueId: `vid_${device.vendorId}_pid_${device.productId}`,
        // Timestamp para referencia
        lastConnected: new Date().toISOString(),
        // Por defecto, no está ignorado
        ignored: false
      };

      localStorage.setItem('printerDevice', JSON.stringify(printerInfo));
      console.log('PrinterService: connectToDetectedPrinter - Configuración completa guardada en localStorage:', JSON.stringify(printerInfo));

      // Actualizar el estado en NgRx
      this.store.dispatch(connectPrinterSuccess({printer: device}));
      console.log('✅ Conectado a:', device.productName || printerInfo.uniqueId);
      return true;
    } catch (error) {
      console.error('❌ Error al conectar a la impresora detectada:', error);
      this.store.dispatch(connectPrinterFailure({error}));
      return false;
    }
  }

  /**
   * Intenta conectarse a un dispositivo guardado previamente
   *
   * Nota: Debido a restricciones de seguridad de WebUSB, este método solo funcionará si:
   * 1. El navegador no se ha cerrado desde que se dio permiso al dispositivo inicialmente
   * 2. O si el dispositivo ya tiene permisos persistentes (no estándar en todos los navegadores)
   */
  /**
   * Marca la impresora como "ignorada" para que no se intente reconectar automáticamente
   * Esta configuración persiste entre recargas de página y cambios de ruta
   */
  ignorePrinter(): void {
    const printerInfoString = localStorage.getItem('printerDevice');
    if (printerInfoString) {
      try {
        const printerInfo = JSON.parse(printerInfoString);

        // Marcar como ignorada
        printerInfo.ignored = true;

        // Guardar la configuración actualizada
        localStorage.setItem('printerDevice', JSON.stringify(printerInfo));

        // Marcar como ignorada en la sesión actual
        PrinterService.printerIgnoredInSession = true;

        console.log('Impresora marcada como ignorada. No se intentará la reconexión automática durante esta sesión.');

        // Desconectar la impresora actual si está conectada
        if (this.device) {
          this.device = null;
          this.endpointNumber = null;
          this.store.dispatch(loadPrinterFailure({error: 'Impresora ignorada por el usuario'}));
        }
      } catch (e) {
        console.error('Error al procesar la información de la impresora guardada:', e);
      }
    } else {
      // Si no hay información de impresora guardada, simplemente marcar como ignorada en la sesión
      PrinterService.printerIgnoredInSession = true;
      console.log('No hay información de impresora guardada, pero se ha marcado como ignorada para esta sesión.');
    }
  }

  async connectToStoredDevice(): Promise<boolean> {
    // Verificar primero si la impresora ha sido ignorada en esta sesión
    if (PrinterService.printerIgnoredInSession) {
      console.log('Impresora ignorada en esta sesión. No se intentará la reconexión automática.');
      this.store.dispatch(loadPrinterFailure({error: 'Impresora ignorada por el usuario'}));
      return false;
    }

    const printerInfoString = localStorage.getItem('printerDevice');
    if (printerInfoString) {
      try {
        console.log('🔄 Intentando reconectar al dispositivo guardado:', printerInfoString);
        const printerInfo = JSON.parse(printerInfoString);

        // Verificar si el usuario ha marcado la impresora como "ignorada" en localStorage
        if (printerInfo.ignored) {
          console.log('Impresora marcada como ignorada en localStorage. No se intentará la reconexión automática.');
          PrinterService.printerIgnoredInSession = true; // Actualizar también la variable de sesión
          this.store.dispatch(loadPrinterFailure({error: 'Impresora ignorada por el usuario'}));
          return false;
        }

        const devices = await (navigator as any).usb.getDevices();
        console.log('📊 Dispositivos con permiso disponibles:', devices.length);

        // Si no se encuentran dispositivos, probablemente es porque el navegador
        // ha perdido los permisos al reiniciarse (comportamiento normal de WebUSB)
        if (devices.length === 0) {
          console.log('⚠️ No se encontraron dispositivos con permisos. El navegador puede haber reiniciado la sesión de WebUSB.');
          this.store.dispatch(loadPrinterFailure({error: 'Dispositivo guardado no encontrado - Requiere reautorización'}));
          return false;
        }

        // Buscar el dispositivo usando múltiples identificadores
        const storedDevice = devices.find((device: USBDevice) => {
          // Verificar si estamos usando el formato antiguo (solo printerId)
          if (printerInfo.printerId) {
            return device.productName === printerInfo.printerId ||
                   device.serialNumber === printerInfo.printerId ||
                   `vid_${device.vendorId}_pid_${device.productId}` === printerInfo.printerId;
          }

          // Usar el nuevo formato con múltiples identificadores
          return (
            // Coincidencia exacta por vendorId y productId (más confiable)
            (device.vendorId === printerInfo.vendorId && device.productId === printerInfo.productId) ||
            // Coincidencia por serialNumber si está disponible
            (printerInfo.serialNumber && device.serialNumber === printerInfo.serialNumber) ||
            // Coincidencia por productName si está disponible y no es "Dispositivo desconocido"
            (printerInfo.productName &&
             device.productName &&
             device.productName !== 'Dispositivo desconocido' &&
             device.productName === printerInfo.productName) ||
            // Coincidencia por uniqueId como último recurso
            `vid_${device.vendorId}_pid_${device.productId}` === printerInfo.uniqueId
          );
        });

        if (storedDevice) {
          await storedDevice.open();
          await this.selectConfigurationAndInterface(storedDevice);
          await this.reset(storedDevice);
          this.device = storedDevice;

          // Actualizar la información almacenada con los datos más recientes
          const updatedPrinterInfo = {
            productName: storedDevice.productName || 'Dispositivo sin nombre',
            serialNumber: storedDevice.serialNumber || '',
            vendorId: storedDevice.vendorId,
            productId: storedDevice.productId,
            uniqueId: `vid_${storedDevice.vendorId}_pid_${storedDevice.productId}`,
            lastConnected: new Date().toISOString()
          };
          localStorage.setItem('printerDevice', JSON.stringify(updatedPrinterInfo));

          this.store.dispatch(connectPrinterSuccess({printer: storedDevice}));
          console.log('✅ Conectado al dispositivo guardado:', storedDevice.productName || updatedPrinterInfo.uniqueId);
          return true;
        } else {
          console.log('❌ El dispositivo guardado no se encuentra entre los dispositivos con permiso');
          this.store.dispatch(loadPrinterFailure({error: 'Dispositivo guardado no encontrado'}));
          return false;
        }
      } catch (error) {
        console.error('❌ Error al reconectar al dispositivo guardado:', error);
        this.store.dispatch(connectPrinterFailure({error}));
        return false;
      }
    } else {
      // Si no hay impresora almacenada en localStorage, despachar loadPrinterFailure
      console.log('❓ No hay configuración de impresora en localStorage');
      this.store.dispatch(loadPrinterFailure({error: 'No printer configuration found'}));
      return false;
    }
  }

  async requestDevice(): Promise<boolean> {
    console.log('PrinterService: requestDevice() called');

    // Verificar primero si la impresora ha sido ignorada en esta sesión
    if (PrinterService.printerIgnoredInSession) {
      console.log('Impresora ignorada en esta sesión. No se iniciará la solicitud de dispositivo.');
      this.store.dispatch(loadPrinterFailure({error: 'Impresora ignorada por el usuario'}));
      return false;
    }

    // Comprobar si tenemos información de impresora guardada
    let savedPrinterInfo: any = null;
    const printerInfoString = localStorage.getItem('printerDevice');

    if (printerInfoString) {
      try {
        savedPrinterInfo = JSON.parse(printerInfoString);
        console.log('🔄 Información de impresora guardada:', savedPrinterInfo);

        // Verificar si el usuario ha marcado la impresora como "ignorada" en localStorage
        if (savedPrinterInfo.ignored) {
          console.log('Impresora marcada como ignorada en localStorage. No se iniciará la solicitud de dispositivo.');
          PrinterService.printerIgnoredInSession = true; // Actualizar también la variable de sesión
          this.store.dispatch(loadPrinterFailure({error: 'Impresora ignorada por el usuario'}));
          return false;
        }
      } catch (e) {
        console.warn('Could not parse printerDevice from localStorage', e);
      }
    }

    try {
      // Solicitar explícitamente permiso y mostrar diálogo de selección USB
      console.log('🔍 Solicitando permiso para acceder a un dispositivo USB...');

      // WebUSB no permite filtrar por nombre, solo podemos usar vendorId y productId
      // Por lo tanto, mostramos una lista completa pero con instrucciones claras para el usuario
      if (savedPrinterInfo) {
        const deviceName = savedPrinterInfo.productName ||
                          (savedPrinterInfo.printerId ? savedPrinterInfo.printerId :
                          savedPrinterInfo.uniqueId || 'Dispositivo guardado');

        console.log('🎯 Dispositivo previamente configurado:', deviceName);
        console.log('🔍 Instrucciones para el usuario: Por favor, seleccione', deviceName);
      }

      let device: USBDevice;
      if (this.device) {
        console.log('✅ Reutilizando dispositivo USB existente:', this.device);
        device = this.device;
      } else {
        console.log('🔍 Solicitando permiso para acceder a un dispositivo USB...');
        device = await (navigator as any).usb.requestDevice({
          filters: [] // Sin filtros para permitir seleccionar cualquier dispositivo
        });
        console.log('✅ Dispositivo seleccionado:', device.productName);
        this.device = device;
      }

      // Verificación informativa - es posible que sea diferente al anterior
      if (savedPrinterInfo && savedPrinterInfo.productName &&
          device.productName !== savedPrinterInfo.productName &&
          device.productName !== 'Dispositivo desconocido') {
        console.log('⚠️ Aviso: El dispositivo seleccionado es diferente al configurado anteriormente');
        console.log(`   Anterior: ${savedPrinterInfo.productName}`);
        console.log(`   Nuevo: ${device.productName}`);
      }

      try {
        await device.open();
        await this.selectConfigurationAndInterface(device);
        await this.reset(device);

        // Guardar la información completa del dispositivo en localStorage
        const printerInfo = {
          productName: device.productName || 'Dispositivo sin nombre',
          serialNumber: device.serialNumber || '',
          vendorId: device.vendorId,
          productId: device.productId,
          uniqueId: `vid_${device.vendorId}_pid_${device.productId}`,
          lastConnected: new Date().toISOString()
        };

        localStorage.setItem('printerDevice', JSON.stringify(printerInfo));
        console.log('PrinterService: requestDevice - Configuración completa guardada en localStorage:', JSON.stringify(printerInfo));

        this.store.dispatch(connectPrinterSuccess({printer: device}));
        console.log('✅ Conectado a:', device.productName || printerInfo.uniqueId);
        return true;
      } catch (connectionError) {
        console.error('❌ Error al configurar el dispositivo seleccionado:', connectionError);
        this.store.dispatch(connectPrinterFailure({error: connectionError}));
        return false;
      }
    } catch (error) {
      // El usuario canceló la selección o no hay dispositivos disponibles
      console.error('❌ No se pudo seleccionar un dispositivo USB:', error);
      this.store.dispatch(connectPrinterFailure({error: 'No se seleccionó ningún dispositivo'}));
      return false;
    }
  }

  private async selectConfigurationAndInterface(device: USBDevice): Promise<void> {
    try {
      const configuration = device.configurations[0];
      console.log('PrinterService: selectConfigurationAndInterface - Seleccionando configuración:', configuration);
      await device.selectConfiguration(configuration.configurationValue);
      console.log('PrinterService: selectConfigurationAndInterface - Configuración seleccionada.');
      for (const iface of configuration.interfaces) {
        for (const alt of iface.alternates) {
          try {
            console.log(`PrinterService: selectConfigurationAndInterface - Reclamando interfaz: ${iface.interfaceNumber}, ajuste: ${alt.alternateSetting}`);
            await device.claimInterface(iface.interfaceNumber);
            await device.selectAlternateInterface(iface.interfaceNumber, alt.alternateSetting);
            console.log(`✅ Reclamada y seleccionada interfaz: ${iface.interfaceNumber}, ajuste: ${alt.alternateSetting}`);
            for (const endpoint of alt.endpoints) {
              if (endpoint.direction === 'out') {
                this.endpointNumber = endpoint.endpointNumber;
                console.log('✅ Endpoint OUT encontrado: ${this.endpointNumber}');
                return;
              }
            }
          } catch (error) {
            console.error(`❌ Error al reclamar interfaz: ${iface.interfaceNumber}, ajuste: ${alt.alternateSetting}`);
          }
        }
      }
      throw new Error('No se pudo reclamar ninguna interfaz.');
    } catch (error) {
      console.error('❌ Error al seleccionar configuración e interfaz:', error);
      throw error;
    }
  }

  private async reset(device: USBDevice): Promise<void> {
    try {
      await this.sendBytes(device, new Uint8Array([this.ESC, 0x40]));
    } catch (error) {
      console.log(error);
    }
  }

  async resetAndSetEncoding(device: USBDevice): Promise<void> {
    // Comandos ESC/POS específicos
    const resetCommand = new Uint8Array([0x1b, 0x40]); // ESC @ (reset)
    const setCodePageCP1252 = new Uint8Array([0x1b, 0x74, 16]); // ESC t 16 (CP-1252)

    try {
      await this.sendBytes(device, resetCommand); // Reiniciar la impresora
      await this.sendBytes(device, setCodePageCP1252); // Configurar la página de códigos a CP-1252
    } catch (error) {
      console.error('❌ Error al enviar comandos de configuración:', error);
    }
  }

  async sendText(str: string): Promise<void> {
    if (!this.device) throw new Error("Device not connected");
    const bytes = new Uint8Array(this.stringToBytes(str));
    await this.sendBytes(this.device, bytes);
  }

  private async sendBytes(device: USBDevice, bytes: Uint8Array): Promise<void> {
    if (this.endpointNumber !== null) {
      await device.transferOut(this.endpointNumber, bytes);
    } else {
      throw new Error('Endpoint number is not set.');
    }
  }

  stringToBytes(str: string): number[] {
    // Mapa de codificación CP1252 - Simplificado usando códigos de escape para caracteres problemáticos
    const cp1252Map: Record<string, number> = {
      '€': 0x80, '\u201A': 0x82, 'ƒ': 0x83, '\u201E': 0x84, '…': 0x85, '†': 0x86, '‡': 0x87, 'ˆ': 0x88, '‰': 0x89, 'Š': 0x8A,
      '‹': 0x8B, 'Œ': 0x8C, 'Ž': 0x8E, '\u2018': 0x91, '\u2019': 0x92, '\u201C': 0x93, '\u201D': 0x94, '•': 0x95, '–': 0x96, '—': 0x97,
      '˜': 0x98, '™': 0x99, 'š': 0x9A, '›': 0x9B, 'œ': 0x9C, 'ž': 0x9E, 'Ÿ': 0x9F, ' ': 0xA0, '¡': 0xA1, '¢': 0xA2,
      '£': 0xA3, '¤': 0xA4, '¥': 0xA5, '¦': 0xA6, '§': 0xA7, '¨': 0xA8, '©': 0xA9, 'ª': 0xAA, '«': 0xAB, '¬': 0xAC,
      '­': 0xAD, '®': 0xAE, '¯': 0xAF, '°': 0xB0, '±': 0xB1, '²': 0xB2, '³': 0xB3, '´': 0xB4, 'µ': 0xB5, '¶': 0xB6,
      '·': 0xB7, '¸': 0xB8, '¹': 0xB9, 'º': 0xBA, '»': 0xBB, '¼': 0xBC, '½': 0xBD, '¾': 0xBE, '¿': 0xBF, 'À': 0xC0,
      'Á': 0xC1, 'Â': 0xC2, 'Ã': 0xC3, 'Ä': 0xC4, 'Å': 0xC5, 'Æ': 0xC6, 'Ç': 0xC7, 'È': 0xC8, 'É': 0xC9, 'Ê': 0xCA,
      'Ë': 0xCB, 'Ì': 0xCC, 'Í': 0xCD, 'Î': 0xCE, 'Ï': 0xCF, 'Ð': 0xD0, 'Ñ': 0xD1, 'Ò': 0xD2, 'Ó': 0xD3, 'Ô': 0xD4,
      'Õ': 0xD5, 'Ö': 0xD6, '×': 0xD7, 'Ø': 0xD8, 'Ù': 0xD9, 'Ú': 0xDA, 'Û': 0xDB, 'Ü': 0xDC, 'Ý': 0xDD, 'Þ': 0xDE,
      'ß': 0xDF, 'à': 0xE0, 'á': 0xE1, 'â': 0xE2, 'ã': 0xE3, 'ä': 0xE4, 'å': 0xE5, 'æ': 0xE6, 'ç': 0xE7, 'è': 0xE8,
      'é': 0xE9, 'ê': 0xEA, 'ë': 0xEB, 'ì': 0xEC, 'í': 0xED, 'î': 0xEE, 'ï': 0xEF, 'ð': 0xF0, 'ñ': 0xF1, 'ò': 0xF2,
      'ó': 0xF3, 'ô': 0xF4, 'õ': 0xF5, 'ö': 0xF6, '÷': 0xF7, 'ø': 0xF8, 'ù': 0xF9, 'ú': 0xFA, 'û': 0xFB, 'ü': 0xFC,
      'ý': 0xFD, 'þ': 0xFE, 'ÿ': 0xFF
    };

    const bytes: number[] = [];
    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      if (cp1252Map[char]) {
        bytes.push(cp1252Map[char]);
      } else {
        bytes.push(char.charCodeAt(0));
      }
    }
    return bytes;
  }


  async printTicket(items: { description: string; price: string }[]): Promise<void> {
    if (!this.device) throw new Error("Device not connected");
    await this.printHeader();
    await this.printSeparator();
    for (const item of items) {
      const line = this.formatLine(item.description, item.price);
      await this.sendText(line + '\n');
    }
    await this.printSeparator();
    await this.sendText('TOTAL: ' + items.reduce((acc, item) => acc + parseFloat(item.price), 0).toFixed(2) + '\n');
  }

  private formatLine(description: string, price: string): string {
    if (description.length + price.length > this.CHARACTERS_PER_LINE) {
      description = description.slice(0, this.CHARACTERS_PER_LINE - price.length - 1);
    }
    const spaces = this.CHARACTERS_PER_LINE - description.length - price.length;
    return description + ' '.repeat(spaces) + price;
  }

  private async printHeader(): Promise<void> {
    const headerLines = [
      "CANGAS AVENTURA S.L",
      "FINCA DE LA DEHESA - EL PORTAZGO, COVIELLA",
      "33540 - CANGAS DE ONIS (ASTURIAS)",
      "TELÉFONO: 985 84 82 61"
    ];

    for (const line of headerLines) {
      await this.sendText(this.centerText(line) + '\n');
    }
  }

  async printFullTicket(ticket: string[]): Promise<void> {
    if (!this.device) throw new Error("Device not connected");

    try {
      // Garantizar que siempre se resuelva la promesa cuando complete
      await this.resetAndSetEncoding(this.device);

      for (const line of ticket) {
        await this.sendText(line + '\n');
      }
      await this.cutPaper();
      await this.openDrawer();

      return Promise.resolve(); // Devolver una promesa explícitamente para poder encadenar .then()
    } catch (error) {
      console.error('Error al imprimir ticket completo:', error);
      throw error; // Re-lanzar para que se pueda manejar en el catch
    }
  }

  async cutPaper(): Promise<void> {
    if (!this.device) throw new Error("Device not connected");
    await this.sendBytes(this.device, this.CUT_PAPER);
  }

  async openDrawer(): Promise<void> {
    if (!this.device) throw new Error("Device not connected");
    return await this.sendBytes(this.device, this.OPEN_DRAWER);
  }

  async printTest(): Promise<void> {
    if (!this.device) {
      alert("Please connect to the printer first.");
      return;
    }
    await this.printHeader();
    await this.printSeparator();
    await this.sendText("Line 1: Hello, World!\n");
    await this.printSeparator();
    await this.sendText("Line 2: This is a test.\n");
    await this.printSeparator();
    await this.sendText("Line 3: Thank you!\n");
    await this.printSeparator();
  }

  private centerText(text: string): string {
    const spaces = Math.floor((this.CHARACTERS_PER_LINE - text.length) / 2);
    return ' '.repeat(spaces) + text + ' '.repeat(spaces);
  }

  private async printSeparator(): Promise<void> {
    const separator = '-'.repeat(this.CHARACTERS_PER_LINE) + '\n';
    await this.sendText(separator);
  }
}


interface Navigator {
  usb: {
    requestDevice(options: { filters: any[] }): Promise<USBDevice>;
    getDevices(): Promise<USBDevice[]>;
  };
}

// Declara la interfaz USBDevice si no está disponible en el entorno actual
interface USBDevice {
  serialNumber: string;
  productName: string;
  vendorId: number;
  productId: number;
  configurations: USBConfiguration[];
  open(): Promise<void>;
  claimInterface(interfaceNumber: number): Promise<void>;
  selectConfiguration(configurationValue: number): Promise<void>;
  selectAlternateInterface(interfaceNumber: number, alternateSetting: number): Promise<void>;
  transferOut(endpointNumber: number, data: BufferSource): Promise<USBOutTransferResult>;
}

interface USBConfiguration {
  configurationValue: number;
  interfaces: USBInterface[];
}

interface USBInterface {
  interfaceNumber: number;
  alternates: USBAlternateInterface[];
  claimed: boolean;
}

interface USBAlternateInterface {
  alternateSetting: number;
  endpoints: USBEndpoint[];
}

interface USBEndpoint {
  endpointNumber: number;
  direction: 'in' | 'out';
}

interface USBOutTransferResult {
  status: string;
}
